import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPayrollFilterByDateRange } from "../../../Redux/Slice/Payroll";
import Loader from "../../../Components/Loader/Loader";
import axios from "axios";
import {
  Calendar,
  User,
  Clock,
  Coffee,
  Filter,
  Briefcase,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  CalendarDays,
  FileSpreadsheet,
  Palmtree,
  Info,
  ArrowDownRight,
  ArrowUpRight
} from "lucide-react";

// JUNIOR / INHOUSE PAYROLL FILTER

const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

const formatMillisecondsToHHMM = (ms, prefix = "") => {
  if (ms < 0) {
    prefix = "-";
    ms = Math.abs(ms);
  }
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return `${prefix}${hours}h ${minutes}m`;
};

const InhouseMonthlyPayroll = ({ memberOptions, selectedMember }) => {
  const dispatch = useDispatch();

  const { FilteredData } = useSelector((state) => state.Payroll);
  const { leaveData } = useSelector((state) => state.LeaveForm || { leaveData: [] });

  const [loading, setLoading] = useState(false);
  const [holidays, setHolidays] = useState([]);

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const res = await axios.get(`${BaseUrl}/Chat/holidays`);
        setHolidays(res.data?.data || []);
      } catch (err) {
        console.error("Error fetching holidays in InhouseMonthlyPayroll:", err);
      }
    };
    fetchHolidays();
  }, []);

  const holidayMap = useMemo(() => {
    const map = new Map();
    if (Array.isArray(holidays)) {
      holidays.forEach((h) => {
        if (h.dateStr) map.set(h.dateStr, h.name || "Company Holiday");
      });
    }
    return map;
  }, [holidays]);

  const selectedMemberOption = useMemo(() => {
    const val =
      typeof selectedMember === "object" && selectedMember !== null
        ? selectedMember.value
        : selectedMember;
    return memberOptions?.find((m) => String(m.value) === String(val));
  }, [memberOptions, selectedMember]);

  // Default toDate = Yesterday (Last completed day) to prevent today's unfinished work penalty
  // Default fromDate = 1st of Yesterday's month
  const [fromDate, setFromDate] = useState(() => {
    const today = new Date();
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    const year = yesterday.getFullYear();
    const month = String(yesterday.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}-01`;
  });

  const [toDate, setToDate] = useState(() => {
    const today = new Date();
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    const year = yesterday.getFullYear();
    const month = String(yesterday.getMonth() + 1).padStart(2, "0");
    const day = String(yesterday.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
  };

  useEffect(() => {
    const memberIdObj =
      typeof selectedMember === "object"
        ? selectedMember
        : memberOptions.find((m) => m.value === selectedMember);

    if (memberIdObj && fromDate && toDate) {
      dispatch(
        fetchPayrollFilterByDateRange({
          memberId: memberIdObj,
          fromDate,
          toDate,
        })
      );
    }
  }, [selectedMember, memberOptions, fromDate, toDate, dispatch]);

  const handleSubmit = async () => {
    const memberId =
      typeof selectedMember === "object"
        ? selectedMember
        : memberOptions.find((m) => m.value === selectedMember);

    if (!memberId) {
      alert("Please select a member first!");
      return;
    }
    if (!fromDate || !toDate) {
      alert("Please select a date range!");
      return;
    }

    setLoading(true);

    try {
      await dispatch(
        fetchPayrollFilterByDateRange({
          memberId,
          fromDate,
          toDate,
        })
      );
    } catch (error) {
      alert("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const calculateMonthlyTotals = () => {
    if (!FilteredData?.data || !Array.isArray(FilteredData.data) || FilteredData.data.length === 0) {
      return null;
    }

    let totalWorkingMs = 0;
    let totalBreakMs = 0;
    let totalExcessBreakMs = 0;
    let totalLateInMs = 0;
    let totalEarlyOutMs = 0;
    let lateInDaysCount = 0;
    let earlyOutDaysCount = 0;
    let absentDaysCount = 0;

    const LUNCH_BREAK_STANDARD_MS = 30 * 60 * 1000;
    const COMPANY_STANDARD_WORK_HOURS_MS = (8 * 60 + 30) * 60 * 1000;

    const checkInTime = selectedMemberOption?.CheckInTime || "10:00";
    const checkOutTime = selectedMemberOption?.CheckOutTime || "19:00";
    const [hIn, mIn] = checkInTime.split(":").map(Number);
    const targetInMinutes = (isNaN(hIn) ? 10 : hIn) * 60 + (isNaN(mIn) ? 0 : mIn);
    const [hOut, mOut] = checkOutTime.split(":").map(Number);
    const targetOutMinutes = (isNaN(hOut) ? 19 : hOut) * 60 + (isNaN(mOut) ? 0 : mOut);

    const dailyPunches = {};

    FilteredData.data.forEach((item) => {
      if (!item) return;

      if (item.InDate) {
        try {
          const d = new Date(item.InDate);
          if (!isNaN(d.getTime())) {
            const dateKey = d.toISOString().slice(0, 10);
            if (!dailyPunches[dateKey]) dailyPunches[dateKey] = [];
            dailyPunches[dateKey].push({
              type: "in",
              time: d,
              original: item.InDate,
            });
          }
        } catch (e) {
          console.error("Invalid InDate:", e);
        }
      }

      if (item.OutDate) {
        try {
          const d = new Date(item.OutDate);
          if (!isNaN(d.getTime())) {
            const dateKey = d.toISOString().slice(0, 10);
            if (!dailyPunches[dateKey]) dailyPunches[dateKey] = [];
            dailyPunches[dateKey].push({
              type: "out",
              time: d,
              original: item.OutDate,
            });
          }
        } catch (e) {
          console.error("Invalid OutDate:", e);
        }
      }
    });

    const selectedMemberName =
      typeof selectedMember === "object" && selectedMember !== null
        ? selectedMember.label
        : memberOptions?.find((m) => m.value === selectedMember)?.label || "";

    const targetName = selectedMemberName.trim().toLowerCase();

    // Generate date range list from fromDate to toDate
    const datesInRange = [];
    if (fromDate && toDate) {
      try {
        let curr = new Date(fromDate);
        const end = new Date(toDate);
        curr.setUTCHours(0, 0, 0, 0);
        end.setUTCHours(0, 0, 0, 0);

        while (curr <= end) {
          datesInRange.push(curr.toISOString().slice(0, 10));
          curr.setUTCDate(curr.getUTCDate() + 1);
        }
      } catch (e) {
        console.error("Error generating dates range:", e);
      }
    }

    const todayStr = new Date().toISOString().slice(0, 10);
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const checkoutMinutes = 19 * 60; // 7:00 PM

    let totalCalendarDaysCount = 0;
    let sundayCount = 0;
    let holidayCount = 0;
    let approvedLeaveCount = 0;
    let effectiveWorkingDaysCount = 0;
    let actualPunchedDaysCount = 0;
    let workedOnHolidaysCount = 0;
    let presentWorkingDaysCount = 0;

    datesInRange.forEach((dateStr) => {
      const isTodayRunning = (dateStr === todayStr && currentMinutes < checkoutMinutes);
      if (isTodayRunning) return; // Exclude today if it's currently running

      totalCalendarDaysCount += 1;

      const hasPunch = Boolean(dailyPunches[dateStr] && dailyPunches[dateStr].length > 0);
      const punchesForDay = hasPunch
        ? dailyPunches[dateStr].sort((a, b) => a.time.getTime() - b.time.getTime())
        : [];

      if (hasPunch) {
        actualPunchedDaysCount += 1;
      }

      const dObj = new Date(dateStr);
      const dayOfWeek = dObj.getUTCDay(); // 0 = Sunday
      const isSunday = dayOfWeek === 0;
      const isHoliday = holidayMap.has(dateStr);

      const isApprovedLeave = leaveData?.some((lv) => {
        const isApproved = lv.Status === "Approved" || lv.status === "Approved";
        if (!isApproved) return false;
        const appName = (lv.applicantName || lv.applicant?.Name || "").trim().toLowerCase();
        return appName === targetName && lv.fromDate <= dateStr && lv.toDate >= dateStr;
      });

      if (isSunday) sundayCount += 1;
      if (isHoliday && !isSunday) holidayCount += 1;
      if (isApprovedLeave && !isSunday && !isHoliday) approvedLeaveCount += 1;

      // Count effective working days (Exclude Sundays, Company Holidays, Approved Leaves)
      const isRequiredWorkingDay = !isSunday && !isHoliday && !isApprovedLeave;
      if (isRequiredWorkingDay) {
        effectiveWorkingDaysCount += 1;
        if (hasPunch) {
          presentWorkingDaysCount += 1;
        } else {
          absentDaysCount += 1;
        }
      } else {
        if (hasPunch) {
          workedOnHolidaysCount += 1;
        }
      }

      // Process day's work, breaks, late-in, and early-out
      let dayWorkMs = 0;
      let dayBreakMs = 0;
      let firstInPunch = null;
      let lastOutPunch = null;
      let lastIn = null;
      let lastOut = null;

      punchesForDay.forEach((punch) => {
        if (punch.type === "in") {
          if (!firstInPunch) firstInPunch = punch;
          if (lastOut && !isNaN(lastOut.getTime())) {
            const breakMs = punch.time.getTime() - lastOut.getTime();
            if (breakMs > 0) dayBreakMs += breakMs;
          }
          lastIn = punch.time;
          lastOut = null;
        } else if (punch.type === "out") {
          lastOutPunch = punch;
          if (lastIn && !isNaN(lastIn.getTime())) {
            const workMs = punch.time.getTime() - lastIn.getTime();
            if (workMs > 0) dayWorkMs += workMs;
            lastIn = null;
          }
          lastOut = punch.time;
        }
      });

      totalWorkingMs += dayWorkMs;
      totalBreakMs += dayBreakMs;

      // Excess break for this day (beyond 30m standard lunch)
      let dayExcessBreakMs = 0;
      if (dayBreakMs > LUNCH_BREAK_STANDARD_MS) {
        dayExcessBreakMs = dayBreakMs - LUNCH_BREAK_STANDARD_MS;
        totalExcessBreakMs += dayExcessBreakMs;
      }

      // Late In calculation for this day (Late arrival)
      if (firstInPunch) {
        const inMins = firstInPunch.time.getUTCHours() * 60 + firstInPunch.time.getUTCMinutes();
        if (inMins > targetInMinutes) {
          const dayLateInMs = (inMins - targetInMinutes) * 60 * 1000;
          totalLateInMs += dayLateInMs;
          lateInDaysCount += 1;
        }
      }

      // Early Out calculation for this day (Early departure)
      if (lastOutPunch) {
        const outMins = lastOutPunch.time.getUTCHours() * 60 + lastOutPunch.time.getUTCMinutes();
        if (outMins < targetOutMinutes) {
          const dayEarlyOutMs = (targetOutMinutes - outMins) * 60 * 1000;
          totalEarlyOutMs += dayEarlyOutMs;
          earlyOutDaysCount += 1;
        }
      }
    });

    if (datesInRange.length === 0) {
      effectiveWorkingDaysCount = Object.keys(dailyPunches).length;
      presentWorkingDaysCount = effectiveWorkingDaysCount;
      totalCalendarDaysCount = effectiveWorkingDaysCount;
    }

    const TOTAL_STANDARD_LUNCH_MS_FOR_PERIOD = effectiveWorkingDaysCount * LUNCH_BREAK_STANDARD_MS;
    const TOTAL_COMPANY_STANDARD_WORK_HOURS_MS_FOR_PERIOD = effectiveWorkingDaysCount * COMPANY_STANDARD_WORK_HOURS_MS;

    let companyBenefitFromBreakMs = 0;
    if (totalBreakMs < TOTAL_STANDARD_LUNCH_MS_FOR_PERIOD) {
      companyBenefitFromBreakMs = TOTAL_STANDARD_LUNCH_MS_FOR_PERIOD - totalBreakMs;
    }

    // Exact Deductions Math:
    // Total Net Shortfall is the EXACT sum of:
    // 1. Late Arrivals (late aya ho)
    // 2. Early Departures (jaldi gaya ho)
    // 3. Excess Break Time (lunch ka 30m minus kar ke jo break bacha)
    // 4. Absent Days (unpunched working days)
    const totalAbsentMs = absentDaysCount * COMPANY_STANDARD_WORK_HOURS_MS;
    const totalDeductionsMs = totalLateInMs + totalEarlyOutMs + totalExcessBreakMs + totalAbsentMs;

    // Net Time Difference vs Standard Schedule (Matches total deductions exactly)
    const netTimeAdjustmentMs = -totalDeductionsMs;

    return {
      totalWorkingTime: formatMillisecondsToHHMM(totalWorkingMs),
      totalBreakTime: formatMillisecondsToHHMM(totalBreakMs),
      adjustedBreakTime: formatMillisecondsToHHMM(totalExcessBreakMs),
      companyBenefitTime: formatMillisecondsToHHMM(companyBenefitFromBreakMs),
      shortfallTime: formatMillisecondsToHHMM(totalDeductionsMs),
      totalDeductionsTime: formatMillisecondsToHHMM(totalDeductionsMs),
      workingDays: effectiveWorkingDaysCount,
      presentWorkingDays: presentWorkingDaysCount,
      calendarDays: totalCalendarDaysCount,
      sundays: sundayCount,
      holidays: holidayCount,
      approvedLeaves: approvedLeaveCount,
      workedOnHolidays: workedOnHolidaysCount,
      punchedDays: actualPunchedDaysCount,
      standardLunchDeduction: formatMillisecondsToHHMM(TOTAL_STANDARD_LUNCH_MS_FOR_PERIOD),
      calculatedStandardWorkHours: formatMillisecondsToHHMM(TOTAL_COMPANY_STANDARD_WORK_HOURS_MS_FOR_PERIOD),
      netTimeAdjustment: netTimeAdjustmentMs,
      // Deductions Breakdown Totals
      totalLateInTime: formatMillisecondsToHHMM(totalLateInMs),
      lateInDays: lateInDaysCount,
      totalEarlyOutTime: formatMillisecondsToHHMM(totalEarlyOutMs),
      earlyOutDays: earlyOutDaysCount,
      absentDays: absentDaysCount,
      totalAbsentTime: formatMillisecondsToHHMM(totalAbsentMs),
      checkInTime,
      checkOutTime,
    };
  };

  const monthlyTotals = calculateMonthlyTotals();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl shadow-sm border border-gray-100 my-6">
        <Loader />
        <p className="mt-4 text-sm font-medium text-gray-500">Calculating monthly payroll summary...</p>
      </div>
    );
  }

  return (
    <div className="w-full bg-slate-50/60 p-4 md:p-6 rounded-2xl border border-slate-200/80 shadow-sm mt-6">
      {/* Header Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
            <FileSpreadsheet className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-800 tracking-tight mb-0.5">
              Monthly Payroll Filter & Analysis
            </h3>
            <p className="text-xs text-slate-500 font-normal">
              Filter employee attendance records to review working hours, late arrivals, early exits, excess breaks & net schedule balance.
            </p>
          </div>
        </div>
      </div>

      {/* Filter Inputs Panel */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          {/* Member Name */}
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-blue-500" />
              Member
            </label>
            <input
              type="text"
              readOnly
              value={
                typeof selectedMember === "object" && selectedMember !== null
                  ? selectedMember.label
                  : memberOptions.find((m) => m.value === selectedMember)?.label || ""
              }
              className="w-full bg-slate-100 border border-slate-300 text-slate-700 text-sm rounded-lg px-3.5 py-2.5 cursor-not-allowed font-medium focus:outline-none"
            />
          </div>

          {/* From Date */}
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-blue-500" />
              From Date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={handleFromDateChange}
              max={toDate || new Date().toISOString().split("T")[0]}
              className="w-full bg-slate-50 border border-slate-300 text-slate-900 text-sm rounded-lg hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition-all px-3.5 py-2.5 cursor-pointer"
            />
          </div>

          {/* To Date */}
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
              <CalendarDays className="w-3.5 h-3.5 text-blue-500" />
              To Date (Last Completed Day)
            </label>
            <input
              type="date"
              value={toDate}
              onChange={handleToDateChange}
              min={fromDate}
              max={new Date().toISOString().split("T")[0]}
              disabled={!fromDate}
              className="w-full bg-slate-50 border border-slate-300 text-slate-900 text-sm rounded-lg hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition-all px-3.5 py-2.5 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>

          {/* Submit Button */}
          <div>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium text-sm rounded-lg py-2.5 px-4 shadow-sm hover:shadow transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
            >
              <Filter className="w-4 h-4" />
              <span>Apply Filter</span>
            </button>
          </div>
        </div>
      </div>

      {/* Monthly Totals Results */}
      {monthlyTotals ? (
        <div className="space-y-6">
          {/* Top KPI Badges */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center gap-3">
              <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Required Work Days</p>
                <h4 className="text-xl font-bold text-slate-800">
                  {monthlyTotals.presentWorkingDays} <span className="text-xs text-slate-400 font-normal">/ {monthlyTotals.workingDays} Days</span>
                </h4>
                {monthlyTotals.absentDays > 0 ? (
                  <p className="text-[11px] text-red-500 font-medium mt-0.5">
                    {monthlyTotals.absentDays} Day{monthlyTotals.absentDays > 1 ? 's' : ''} Absent
                  </p>
                ) : (
                  <p className="text-[11px] text-emerald-600 font-medium mt-0.5">
                    All {monthlyTotals.workingDays} working days attended
                  </p>
                )}
                {monthlyTotals.workedOnHolidays > 0 && (
                  <p className="text-[11px] text-indigo-600 font-medium mt-0.5">
                    (+{monthlyTotals.workedOnHolidays} Weekend/Holiday Worked)
                  </p>
                )}
                {monthlyTotals.holidays > 0 && (
                  <p className="text-[11px] text-emerald-600 font-medium mt-0.5">
                    (+{monthlyTotals.holidays} Company Holiday{monthlyTotals.holidays > 1 ? 's' : ''})
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center gap-3">
              <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Total Work Hours</p>
                <h4 className="text-xl font-bold text-indigo-600">{monthlyTotals.totalWorkingTime}</h4>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center gap-3">
              <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
                <Coffee className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Total Break Time</p>
                <h4 className="text-xl font-bold text-amber-600">{monthlyTotals.totalBreakTime}</h4>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center gap-3">
              <div className={`p-3 rounded-xl ${monthlyTotals.netTimeAdjustment < 0 ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                {monthlyTotals.netTimeAdjustment < 0 ? <TrendingDown className="w-5 h-5" /> : <TrendingUp className="w-5 h-5" />}
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Net Schedule Difference</p>
                <h4 className={`text-xl font-bold ${monthlyTotals.netTimeAdjustment < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {formatMillisecondsToHHMM(monthlyTotals.netTimeAdjustment)}
                </h4>
              </div>
            </div>
          </div>

          {/* Breakdown Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* 1. Working Time Summary */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden flex flex-col justify-between">
              <div>
                <div className="px-5 py-3.5 bg-slate-50/80 border-b border-slate-200 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-blue-600" />
                  <h4 className="text-sm font-bold text-slate-800 tracking-wide uppercase">Working Time Summary</h4>
                </div>
                <div className="p-5 space-y-3 text-sm">
                  <div className="flex justify-between items-center py-1 border-b border-slate-100">
                    <span className="text-slate-600 font-medium">Total Days in Period</span>
                    <span className="font-bold text-slate-800">{monthlyTotals.calendarDays} Days</span>
                  </div>

                  {monthlyTotals.holidays > 0 && (
                    <div className="flex justify-between items-center py-1 border-b border-slate-100 text-xs">
                      <span className="text-slate-600 font-medium flex items-center gap-1">
                        <Palmtree className="w-3.5 h-3.5 text-emerald-500" />
                        Company Holidays (Holiday Master)
                      </span>
                      <span className="font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        +{monthlyTotals.holidays} Holiday{monthlyTotals.holidays > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}

                  {monthlyTotals.sundays > 0 && (
                    <div className="flex justify-between items-center py-1 border-b border-slate-100 text-xs">
                      <span className="text-slate-600 font-medium flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        Sundays (Weekend Offs)
                      </span>
                      <span className="font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                        -{monthlyTotals.sundays} Sunday{monthlyTotals.sundays > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}

                  {monthlyTotals.approvedLeaves > 0 && (
                    <div className="flex justify-between items-center py-1 border-b border-slate-100 text-xs">
                      <span className="text-slate-600 font-medium flex items-center gap-1">
                        <Info className="w-3.5 h-3.5 text-blue-500" />
                        Approved Leaves (Deducted)
                      </span>
                      <span className="font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                        -{monthlyTotals.approvedLeaves} Days
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-center py-1.5 bg-blue-50/50 px-2.5 rounded-lg border border-blue-100 my-1">
                    <span className="text-blue-900 font-bold text-xs">Effective Required Working Days</span>
                    <span className="font-black text-blue-700">{monthlyTotals.workingDays} Days</span>
                  </div>

                  <div className="flex justify-between items-center py-1 border-b border-slate-100 text-xs">
                    <span className="text-slate-600 font-medium flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      Working Days Attended (Present)
                    </span>
                    <span className="font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                      {monthlyTotals.presentWorkingDays} / {monthlyTotals.workingDays} Days
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-1 border-b border-slate-100">
                    <span className="text-slate-600 font-medium">Standard Schedule ({monthlyTotals.workingDays}d × 8h 30m)</span>
                    <span className="font-bold text-slate-700">{monthlyTotals.calculatedStandardWorkHours}</span>
                  </div>

                  <div className="flex justify-between items-center py-1">
                    <span className="text-slate-600 font-medium">Logged Working Time</span>
                    <span className="font-bold text-indigo-600">{monthlyTotals.totalWorkingTime}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Break Time Analysis */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden flex flex-col justify-between">
              <div>
                <div className="px-5 py-3.5 bg-slate-50/80 border-b border-slate-200 flex items-center gap-2">
                  <Coffee className="w-4 h-4 text-amber-600" />
                  <h4 className="text-sm font-bold text-slate-800 tracking-wide uppercase">Break Time Analysis</h4>
                </div>
                <div className="p-5 space-y-3.5 text-sm">
                  <div className="flex justify-between items-center py-1 border-b border-slate-100">
                    <span className="text-slate-600 font-medium">Total Logged Break Duration</span>
                    <span className="font-bold text-amber-600">{monthlyTotals.totalBreakTime}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-slate-100">
                    <span className="text-slate-600 font-medium">Standard Lunch Allowance ({monthlyTotals.workingDays}d × 30m)</span>
                    <span className="font-bold text-slate-700">{monthlyTotals.standardLunchDeduction}</span>
                  </div>
                  {monthlyTotals.adjustedBreakTime !== "0h 0m" && (
                    <div className="flex items-center justify-between gap-2 p-2.5 bg-red-50 text-red-700 rounded-lg border border-red-100">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                        <span className="font-medium text-xs truncate">
                          Adjusted Break Time (Excess)
                        </span>
                      </div>
                      <span className="font-bold text-sm text-red-700 shrink-0 whitespace-nowrap ml-2">
                        {monthlyTotals.adjustedBreakTime}
                      </span>
                    </div>
                  )}
                  {monthlyTotals.companyBenefitTime !== "0h 0m" && (
                    <div className="flex items-center justify-between gap-2 p-2.5 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-100">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                        <span className="font-medium text-xs truncate">
                          Saved Lunch Allowance
                        </span>
                      </div>
                      <span className="font-bold text-sm text-emerald-700 shrink-0 whitespace-nowrap ml-2">
                        {monthlyTotals.companyBenefitTime}
                      </span>
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400 italic">
                    * Lunch break up to 30 mins daily is company allowed. Any break time exceeding 30 mins is deducted from effective work hours.
                  </p>
                </div>
              </div>
            </div>

            {/* 3. Deductions & Shortfall Analysis (100% MATHEMATICALLY MATCHED) */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden flex flex-col justify-between">
              <div>
                <div className="px-5 py-3.5 bg-slate-50/80 border-b border-slate-200 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <h4 className="text-sm font-bold text-slate-800 tracking-wide uppercase">Deductions & Shortfall Breakdown</h4>
                </div>
                <div className="p-5 space-y-3 text-sm">
                  <div className="flex justify-between items-center py-1 border-b border-slate-100">
                    <span className="text-slate-600 font-medium flex items-center gap-1">
                      <ArrowDownRight className="w-3.5 h-3.5 text-amber-500" />
                      Late Arrivals ({monthlyTotals.lateInDays} Days Late)
                    </span>
                    <span className="font-bold text-amber-600">-{monthlyTotals.totalLateInTime}</span>
                  </div>

                  <div className="flex justify-between items-center py-1 border-b border-slate-100">
                    <span className="text-slate-600 font-medium flex items-center gap-1">
                      <ArrowUpRight className="w-3.5 h-3.5 text-orange-500" />
                      Early Departures ({monthlyTotals.earlyOutDays} Days Early)
                    </span>
                    <span className="font-bold text-orange-600">-{monthlyTotals.totalEarlyOutTime}</span>
                  </div>

                  <div className="flex justify-between items-center py-1 border-b border-slate-100">
                    <span className="text-slate-600 font-medium flex items-center gap-1">
                      <Coffee className="w-3.5 h-3.5 text-red-500" />
                      Excess Break Time (Beyond Lunch)
                    </span>
                    <span className="font-bold text-red-600">-{monthlyTotals.adjustedBreakTime}</span>
                  </div>

                  {monthlyTotals.absentDays > 0 && (
                    <div className="flex justify-between items-center py-1 border-b border-slate-100">
                      <span className="text-slate-600 font-medium flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5 text-rose-500" />
                        Absent Days ({monthlyTotals.absentDays} Days Unpunched)
                      </span>
                      <span className="font-bold text-rose-700">-{monthlyTotals.totalAbsentTime}</span>
                    </div>
                  )}

                  {/* Total Net Shortfall matches the exact sum of rows above */}
                  <div className="flex justify-between items-center p-2.5 bg-red-50 text-red-800 rounded-lg border border-red-200 mt-2">
                    <span className="font-bold text-xs">Total Net Shortfall</span>
                    <span className="font-black text-base text-red-600">-{monthlyTotals.totalDeductionsTime}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Net Time Adjustment Banner */}
          <div
            className={`p-4 sm:p-5 rounded-xl border ${
              monthlyTotals.netTimeAdjustment < 0
                ? "bg-gradient-to-r from-red-50 via-rose-50 to-orange-50 border-red-200 text-red-900"
                : "bg-gradient-to-r from-emerald-50 via-teal-50 to-green-50 border-emerald-200 text-emerald-900"
            } shadow-xs flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4`}
          >
            <div className="flex items-start sm:items-center gap-3 min-w-0">
              <div
                className={`p-2.5 rounded-lg shrink-0 ${
                  monthlyTotals.netTimeAdjustment < 0 ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-600"
                }`}
              >
                {monthlyTotals.netTimeAdjustment < 0 ? (
                  <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6" />
                )}
              </div>
              <div className="min-w-0">
                <h4 className="text-sm sm:text-base font-bold tracking-tight mb-0.5">
                  Net Time Difference vs Standard Schedule
                </h4>
                <p className="text-xs font-medium opacity-90 leading-normal">
                  {monthlyTotals.netTimeAdjustment < 0
                    ? "This employee has a deficit of time worked compared to the company standard schedule."
                    : "This employee has a surplus of time worked compared to the company standard schedule."}
                </p>
              </div>
            </div>

            <div className="w-full sm:w-auto shrink-0 px-4 py-2.5 bg-white/95 rounded-xl border border-slate-200/80 shadow-2xs flex sm:flex-col items-center sm:items-end justify-between sm:justify-center">
              <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Net Balance</span>
              <span
                className={`text-lg sm:text-xl font-black whitespace-nowrap ${
                  monthlyTotals.netTimeAdjustment < 0 ? "text-red-600" : "text-emerald-600"
                }`}
              >
                {formatMillisecondsToHHMM(monthlyTotals.netTimeAdjustment)}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white p-8 rounded-xl border border-slate-200 text-center shadow-xs">
          <div className="w-12 h-12 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-3">
            <Filter className="w-6 h-6" />
          </div>
          <h4 className="text-base font-semibold text-slate-700 mb-1">No Monthly Payroll Data Loaded</h4>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            Select a date range above and click <span className="font-semibold text-blue-600">Apply Filter</span> to generate monthly work hours and break analysis.
          </p>
        </div>
      )}
    </div>
  );
};

export default InhouseMonthlyPayroll;
